import * as React from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

/**
 * SearchInput
 * Standardizes mobile keyboard hints for every search field in the app:
 *  - opens the search-style keyboard with a magnifying-glass return key
 *  - disables autocorrect / autocapitalize / spellcheck so queries aren't mangled
 *  - hides the native WebKit "x" decoration so it doesn't clash with our own Clear ✕
 *
 * Drop-in replacement for <Input>; pass any extra HTML input attributes.
 */
export const SearchInput = React.forwardRef<HTMLInputElement, React.ComponentProps<'input'>>(
  ({ className, autoCapitalize = 'none', ...props }, ref) => {
    return (
      <Input
        ref={ref}
        type="search"
        inputMode="search"
        enterKeyHint="search"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize={autoCapitalize}
        spellCheck={false}
        className={cn(
          // Search bars are pill-shaped (distinct from form inputs)
          'rounded-full',
          // Strip the native WebKit search decorations
          '[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none [&::-webkit-search-results-button]:appearance-none [&::-webkit-search-results-decoration]:appearance-none',
          className
        )}
        {...props}
      />
    );
  }
);
SearchInput.displayName = 'SearchInput';
